// The solving settings, shown both on the Settings page and in the puzzle's settings sheet (the
// cog by undo and redo), so the two always offer the same choices, in four groups: Checking,
// Highlights, Pad (layout, buttons, each mode's look) and Other.
import {
  DEFAULT_PAD_LOOKS,
  PAD_LOOK_MODES,
  type PadLook,
  type PadLookMode,
  type PadLooks,
  type PadNumberStyle,
  type UserSettings,
} from '@zinc/shared';
import { useEffect, useRef, useState } from 'react';
import { KeyFace, keyColours } from './solve/Pad';
import { Card, SectionTitle, Segmented, SwitchRow } from './ui';

export type SetSetting = <K extends keyof UserSettings>(key: K) => (value: UserSettings[K]) => void;

export function MistakeChecking({ s, set }: { s: UserSettings; set: SetSetting }) {
  return (
    <Segmented
      label="Mistake checking"
      value={s.mistakeChecking}
      onChange={set('mistakeChecking')}
      options={[
        { value: 'off', label: 'Off' },
        { value: 'conflicts', label: 'Conflicts' },
        { value: 'solution', label: 'Solution' },
      ]}
    />
  );
}

/** What is checked as you go (both modes), at the mistake-checking level. */
export function CheckSwitches({ s, set }: { s: UserSettings; set: SetSetting }) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <SwitchRow label="Check digits" checked={s.checkDigits} onChange={set('checkDigits')} />
      <SwitchRow
        label="Check pencil marks"
        checked={s.checkMarks}
        onChange={set('checkMarks')}
        last
      />
    </Card>
  );
}

export const CHECK_NOTE =
  'Checked as you go, in both modes. Conflicts: a digit or mark that repeats in a row, column or box turns red (both repeats). Solution: also a digit the solution rules out. Pencil marks turn red only for repeats, never from the solution.';

function HighlightSwitches({ s, set }: { s: UserSettings; set: SetSetting }) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <SwitchRow
        label="Highlight matching digits"
        checked={s.highlightMatchingDigits}
        onChange={set('highlightMatchingDigits')}
      />
      <SwitchRow
        label="Highlight matching candidates"
        checked={s.highlightMatchingCandidates}
        onChange={set('highlightMatchingCandidates')}
      />
      <SwitchRow
        label="One tap highlights the digit"
        checked={s.tapHighlightsDigit}
        onChange={set('tapHighlightsDigit')}
      />
      <SwitchRow
        label="Highlight row, column and box"
        checked={s.highlightHouses}
        onChange={set('highlightHouses')}
        last
      />
    </Card>
  );
}

/** Which buttons the pad shows: SudokuPad mode's marks, Fill all candidates, the tools. */
function PadButtonSwitches({ s, set }: { s: UserSettings; set: SetSetting }) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <SwitchRow
        label="Show corner marks"
        checked={s.showCornerMarks}
        onChange={set('showCornerMarks')}
      />
      <SwitchRow
        label="Show centre marks"
        checked={s.showCentreMarks}
        onChange={set('showCentreMarks')}
      />
      <SwitchRow
        label="Show position marks"
        checked={s.showPositionMarks}
        onChange={set('showPositionMarks')}
      />
      <SwitchRow
        label="Show Fill all candidates (1–9)"
        checked={s.showFillButton}
        onChange={set('showFillButton')}
      />
      <SwitchRow
        label="Show the settings button"
        checked={s.showSettingsButton}
        onChange={set('showSettingsButton')}
      />
      <SwitchRow
        label="Show the sum tool (Σ)"
        checked={s.showHelper}
        onChange={set('showHelper')}
      />
      <SwitchRow
        label="Show the select tool"
        checked={s.showSelectTool}
        onChange={set('showSelectTool')}
      />
      <SwitchRow
        label="Show the colour tool"
        checked={s.showColourTool}
        onChange={set('showColourTool')}
      />
      <SwitchRow
        label="Show the pen tool"
        checked={s.showPenTool}
        onChange={set('showPenTool')}
        last
      />
    </Card>
  );
}

export const TOOLS_NOTE =
  'Corner, centre and position marks are SudokuPad mode’s; a hidden one keeps its keyboard keys. 1–9 and the settings cog sit next to undo and redo (with the cog hidden, the puzzle’s settings are in the ⋯ menu); select, colour and pen next to the mark buttons (colour and pen in SudokuPad mode).';

function OtherSwitches({ s, set }: { s: UserSettings; set: SetSetting }) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <SwitchRow
        label="Clear notes when a digit is placed"
        checked={s.clearNotesOnPlacement}
        onChange={set('clearNotesOnPlacement')}
      />
      <SwitchRow label="Hide timer" checked={s.hideTimer} onChange={set('hideTimer')} />
      <SwitchRow
        label="Keep screen on while solving"
        checked={s.keepScreenOn}
        onChange={set('keepScreenOn')}
        last
      />
    </Card>
  );
}

const MODE_LABELS: Record<PadLookMode, string> = {
  digit: 'Digits',
  corner: 'Corner marks',
  centre: 'Centre marks',
  position: 'Position marks',
  pencil: 'Pencil marks (Simple mode)',
};

const STYLES: readonly { value: PadNumberStyle; label: string }[] = [
  { value: 'big', label: 'Big' },
  { value: 'corner', label: 'Corner' },
  { value: 'centre', label: 'Middle' },
  { value: 'position', label: 'Own spot' },
];

/** The app's own key and number colours (the Surface and Ink tokens), for the colour pickers. */
function tokenColour(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return /^#[0-9a-f]{6}$/i.test(v) ? v : fallback;
}

/** Keys 1, 5 and 9 as the pad draws them in this look. */
function LookPreview({ look }: { look: PadLook }) {
  return (
    <span className="flex gap-1.5" aria-hidden="true">
      {['1', '5', '9'].map((d) => (
        <span
          key={d}
          className="relative flex h-10 w-10 items-center justify-center rounded-[10px] border border-line bg-surface text-[20px] font-medium text-ink"
          style={keyColours(look)}
        >
          <KeyFace
            digit={d}
            index={Number(d) - 1}
            side={3}
            style={look.style}
            markSize="text-[11px]"
          />
        </span>
      ))}
    </span>
  );
}

function ColourChoice({
  label,
  value,
  fallback,
  onPick,
  onDefault,
}: {
  label: string;
  value: string | null;
  fallback: string;
  onPick: (colour: string) => void;
  onDefault: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex-1 text-[15px]">{label}</span>
      <input
        type="color"
        aria-label={label}
        value={value ?? fallback}
        onChange={(e) => onPick(e.target.value)}
        className="h-10 w-14 cursor-pointer rounded-btn border border-line bg-surface p-1"
      />
      <button
        type="button"
        disabled={value === null}
        onClick={onDefault}
        className="h-10 rounded-btn border border-line bg-surface px-3 text-[14px] font-semibold text-ink disabled:opacity-40"
      >
        Default
      </button>
    </div>
  );
}

/**
 * Pad look: for each mode, where the number sits on its key and the key's and number's colours
 * (any colour: Marko, 2026-11-01), with the keys drawn as the pad draws them. Colours are saved
 * a moment after the picker settles, so dragging through it doesn't send every shade.
 */
export function PadLookEditor({
  looks,
  onChange,
}: {
  looks: PadLooks;
  onChange: (looks: PadLooks) => void;
}) {
  const [open, setOpen] = useState<PadLookMode | null>(null);
  const [draft, setDraft] = useState<PadLooks | null>(null);
  const pending = useRef<{ timer: number; looks: PadLooks } | null>(null);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  // Leaving with a colour still waiting saves it.
  useEffect(
    () => () => {
      if (pending.current) {
        window.clearTimeout(pending.current.timer);
        onChangeRef.current(pending.current.looks);
      }
    },
    [],
  );
  const shown = draft ?? looks;
  const save = (next: PadLooks, later = false) => {
    if (pending.current) window.clearTimeout(pending.current.timer);
    pending.current = null;
    if (!later) {
      setDraft(null);
      onChange(next);
      return;
    }
    setDraft(next);
    pending.current = {
      looks: next,
      timer: window.setTimeout(() => {
        pending.current = null;
        setDraft(null);
        onChangeRef.current(next);
      }, 400),
    };
  };
  const change = (mode: PadLookMode, patch: Partial<PadLook>, later = false) =>
    save({ ...shown, [mode]: { ...shown[mode], ...patch } }, later);
  const keyDefault = tokenColour('--color-surface', '#ffffff');
  const numberDefault = tokenColour('--color-ink', '#1f1e1c');

  return (
    <Card className="flex flex-col overflow-hidden">
      {PAD_LOOK_MODES.map((mode, i) => {
        const look = shown[mode];
        const expanded = open === mode;
        const changed =
          look.key !== null || look.number !== null || look.style !== DEFAULT_PAD_LOOKS[mode].style;
        return (
          <div
            key={mode}
            className={
              i === PAD_LOOK_MODES.length - 1 ? '' : 'border-b border-solid border-line-soft'
            }
          >
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setOpen(expanded ? null : mode)}
              className="flex min-h-14 w-full items-center gap-3 border-0 bg-transparent px-3.5 py-2 text-left text-[15px] text-ink"
            >
              <span className="flex-1">{MODE_LABELS[mode]}</span>
              <LookPreview look={look} />
            </button>
            {expanded && (
              <div
                className="flex flex-col gap-3 px-3.5 pb-3.5"
                role="group"
                aria-label={`${MODE_LABELS[mode]} look`}
              >
                <Segmented
                  label="Number"
                  value={look.style}
                  onChange={(style) => change(mode, { style })}
                  options={STYLES}
                />
                <ColourChoice
                  label="Key colour"
                  value={look.key}
                  fallback={keyDefault}
                  onPick={(key) => change(mode, { key }, true)}
                  onDefault={() => change(mode, { key: null })}
                />
                <ColourChoice
                  label="Number colour"
                  value={look.number}
                  fallback={numberDefault}
                  onPick={(number) => change(mode, { number }, true)}
                  onDefault={() => change(mode, { number: null })}
                />
                <button
                  type="button"
                  disabled={!changed}
                  onClick={() => change(mode, DEFAULT_PAD_LOOKS[mode])}
                  className="self-start border-0 bg-transparent p-0 text-[14px] font-semibold text-accent hover:text-accent-dark disabled:opacity-40"
                >
                  Reset {MODE_LABELS[mode].replace(' (Simple mode)', '').toLowerCase()}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}

/** Pad: blocks or one row (and where the mode buttons go then), which buttons show, the look. */
function PadSettings({ s, set }: { s: UserSettings; set: SetSetting }) {
  return (
    <>
      <Segmented
        label="Pad layout"
        value={s.padLayout}
        onChange={set('padLayout')}
        options={[
          { value: 'blocks', label: 'Blocks of three' },
          { value: 'row', label: 'One row' },
        ]}
      />
      {s.padLayout === 'row' && (
        <Segmented
          label="Mode buttons"
          value={s.padToolsAt}
          onChange={set('padToolsAt')}
          options={[
            { value: 'above', label: 'Above the numbers' },
            { value: 'below', label: 'Below the numbers' },
          ]}
        />
      )}
      <PadButtonSwitches s={s} set={set} />
      <p className="mx-1 my-0 text-[12px] text-muted">{TOOLS_NOTE}</p>
      <SectionTitle>Pad look</SectionTitle>
      <PadLookEditor looks={s.padLooks} onChange={set('padLooks')} />
    </>
  );
}

/** Every solving setting, in its groups (the puzzle's settings sheet and the Settings page). */
export function SolvingSettings({ s, set }: { s: UserSettings; set: SetSetting }) {
  return (
    <>
      <section className="flex flex-col gap-2">
        <SectionTitle>Checking</SectionTitle>
        <MistakeChecking s={s} set={set} />
        <CheckSwitches s={s} set={set} />
        <p className="mx-1 my-0 text-[12px] text-muted">{CHECK_NOTE}</p>
      </section>
      <section className="flex flex-col gap-2">
        <SectionTitle>Highlights</SectionTitle>
        <HighlightSwitches s={s} set={set} />
      </section>
      <section className="flex flex-col gap-2">
        <SectionTitle>Pad</SectionTitle>
        <PadSettings s={s} set={set} />
      </section>
      <section className="flex flex-col gap-2">
        <SectionTitle>Other</SectionTitle>
        <OtherSwitches s={s} set={set} />
      </section>
    </>
  );
}
